import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from '@shared/core';
import type { SurveyStepProps } from '../types';
import { ContactInputFields } from '@apps/contactCollector/guest/ContactCollectorGuest.controller';
import { NATIONAL_COUNTRY } from '@apps/registry/common/components/ShoppingCart/state/ShoppingCart.utils';
import { NATIONAL_COUNTRY_CODE } from '@apps/admin/common/ShippingAddress/constants';
import { FieldVisibility } from '../../EventFormEditor';

type Contacts = { contacts: ContactInputFields[] };

const yupSchema = (tRequired: string, tInvalidEmail: string, phoneNumberRule: FieldVisibility, mailingAddressRule: FieldVisibility) =>
  Yup.object({
    contacts: Yup.array()
      .min(1)
      .of(
        Yup.object<ContactInputFields>({
          firstName: Yup.string().required(tRequired),
          lastName: Yup.string().required(tRequired),
          phoneNumber: phoneNumberRule === 'required' ? Yup.string().required(tRequired) : Yup.string(),
          email: Yup.string().email(tInvalidEmail).required(tRequired),
          address1: mailingAddressRule === 'required' ? Yup.string().required(tRequired) : Yup.string(),
          address2: Yup.string(),
          postalCode: mailingAddressRule === 'required' ? Yup.string().required(tRequired) : Yup.string(),
          city: mailingAddressRule === 'required' ? Yup.string().required(tRequired) : Yup.string(),
          region: mailingAddressRule === 'required' ? Yup.string().required(tRequired) : Yup.string(),
          country: Yup.string(),
          countryCode: Yup.string(),
          validated: Yup.boolean(),
        }),
      ),
  });

export const useContactFormData = (
  settings: SurveyStepProps['settings'],
  setContactData: (contactData: SurveyStepProps['formState']['contactValues']) => void,
  initialData?: SurveyStepProps['formState']['contactValues'],
) => {
  const { tq } = useTranslation('eventFormDisplay');
  const [e164PhoneNumber, setE164PhoneNumber] = useState<{ [key: number]: string }>({ 0: '' });

  const initialValues = useMemo(
    () => ({
      contacts: [
        {
          firstName: initialData?.firstName ?? '',
          lastName: initialData?.lastName ?? '',
          phoneNumber: initialData?.phoneNumber ?? '',
          email: initialData?.email ?? '',
          address1: initialData?.address1 ?? '',
          address2: initialData?.address2 ?? '',
          postalCode: initialData?.postalCode ?? '',
          city: initialData?.city ?? '',
          region: initialData?.region ?? '',
          country: initialData?.country ?? NATIONAL_COUNTRY,
          countryCode: initialData?.countryCode ?? NATIONAL_COUNTRY_CODE,
          validated: false,
        },
      ],
    }),
    [initialData],
  );

  const formik = useFormik<Contacts>({
    enableReinitialize: true,
    initialValues,
    validationSchema: yupSchema(tq`Required`, tq`Invalid email`, settings.phoneFields, settings.addressFields),
    validateOnMount: true,
    onSubmit: async data => {
      // Placeholder - will be implemented when form submission logic is ready
      setContactData(data.contacts[0]);
    },
  });

  return {
    formik,
    e164PhoneNumber,
    setE164PhoneNumber,
  };
};
